import React from 'react';
import { ActionIcon, Button, Group, Select, Stack, TextInput, Tooltip } from '@mantine/core';
import { IconInfoCircle, IconPlus, IconTrash } from '@tabler/icons-react';
import type { CodeBlockOutput } from '../../types/editor';
import type { HandleType } from '../../types/registry';
import classes from './NodeConfigDrawer.module.css';

const OUTPUT_TYPE_OPTIONS: Array<{ value: HandleType; label: string }> = [
  { value: 'any', label: 'any' },
  { value: 'number', label: 'number' },
  { value: 'string', label: 'string' },
  { value: 'boolean', label: 'boolean' },
  { value: 'object', label: 'object' },
  { value: 'array', label: 'array' }
];

const VALID_JS_IDENTIFIER = /^[a-zA-Z_$][a-zA-Z0-9_$]*$/;

const DEFAULT_OUTPUT: CodeBlockOutput = { name: 'output', type: 'any' };

export type CodeBlockOutputsSectionProps = {
  outputs: CodeBlockOutput[];
  onChange: ( outputs: CodeBlockOutput[]) => void;
};

/**
 * Section within the NodeConfigDrawer that lets users configure
 * CodeBlock output handles (name + type). Each output becomes a
 * handle on the node and shapes the return type in the code editor.
 */
export function CodeBlockOutputsSection({
  outputs,
  onChange
}: CodeBlockOutputsSectionProps ) {
  const effectiveOutputs = outputs.length > 0 ? outputs : [DEFAULT_OUTPUT];

  const duplicateNames = React.useMemo(() => {
    const seen = new Set<string>();
    const dupes = new Set<string>();
    for ( const o of effectiveOutputs ) {
      if ( seen.has( o.name )) {
        dupes.add( o.name );
      }
      seen.add( o.name );
    }
    return dupes;
  }, [effectiveOutputs]);

  const handleNameChange = React.useCallback(( index: number, name: string ) => {
    const updated = effectiveOutputs.map(( o, i ) =>
      i === index ? { ...o, name } : o );
    onChange( updated );
  }, [ effectiveOutputs, onChange ]);

  const handleTypeChange = React.useCallback(( index: number, type: string | null ) => {
    if ( !type ) return;
    const updated = effectiveOutputs.map(( o, i ) =>
      i === index ? { ...o, type: type as HandleType } : o );
    onChange( updated );
  }, [ effectiveOutputs, onChange ]);

  const handleDelete = React.useCallback(( index: number ) => {
    if ( effectiveOutputs.length <= 1 ) return;
    const updated = effectiveOutputs.filter(( _, i ) => i !== index );
    onChange( updated );
  }, [ effectiveOutputs, onChange ]);

  const handleAdd = React.useCallback(() => {
    onChange([ ...effectiveOutputs, { name: '', type: 'any' }]);
  }, [ effectiveOutputs, onChange ]);

  return (
    <div>
      <Group gap={6} className={classes.sectionTitle} style={{ marginTop: 16 }}>
        <span>Outputs</span>
        <Tooltip
          label="Declare what this code block returns. Output names become handles on the node."
          multiline
          w={220}
          withArrow
        >
          <IconInfoCircle size={14} style={{ cursor: 'help', opacity: 0.7 }} />
        </Tooltip>
      </Group>

      <Stack gap="xs">
        {effectiveOutputs.map(( output, index ) => {
          const isInvalidName = output.name.length > 0 && !VALID_JS_IDENTIFIER.test( output.name );
          const isDuplicate = duplicateNames.has( output.name ) && output.name.length > 0;

          return (
            <Group key={index} gap="xs" wrap="nowrap">
              <TextInput
                value={output.name}
                onChange={( e ) => handleNameChange( index, e.currentTarget.value )}
                placeholder="outputName"
                size="xs"
                error={isInvalidName || isDuplicate}
                style={{ flex: 1 }}
                styles={{
                  input: {
                    background: 'var(--drawer-input-bg)',
                    borderColor: isInvalidName || isDuplicate
                      ? '#ef4444'
                      : 'var(--drawer-input-border)',
                    color: 'var(--drawer-text)',
                    fontSize: 13
                  }
                }}
              />
              <Select
                value={output.type}
                onChange={( val ) => handleTypeChange( index, val )}
                data={OUTPUT_TYPE_OPTIONS}
                size="xs"
                w={100}
                allowDeselect={false}
                styles={{
                  input: {
                    background: 'var(--drawer-input-bg)',
                    borderColor: 'var(--drawer-input-border)',
                    color: 'var(--drawer-text)',
                    fontSize: 12
                  }
                }}
              />
              <ActionIcon
                variant="subtle"
                color="red"
                size="sm"
                onClick={() => handleDelete( index )}
                disabled={effectiveOutputs.length <= 1}
              >
                <IconTrash size={14} />
              </ActionIcon>
            </Group>
          );
        })}
      </Stack>

      <Button
        variant="light"
        size="xs"
        leftSection={<IconPlus size={14} />}
        onClick={handleAdd}
        mt="xs"
      >
        Add Output
      </Button>
    </div>
  );
}
